import React from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const ROW_KEYS = [
  "r1",
  "r2",
  "r3",
  "r4",
  "r5",
  "r6",
  "r7",
  "r8",
  "r9",
] as const;

const interFont = {
  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
} as const;

export default function MenoCompareV2() {
  const { t, isRTL } = useLanguage();
  const textAlign = "text-start";

  return (
    <section className="bg-white py-16 lg:py-24 safari-paint-contain">
      <div className="custom-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`mb-10 lg:mb-14 ${textAlign}`}
        >
          <span
            className={`inline-flex items-center bg-[#FFE1EC] text-meno-pink text-xs font-bold px-4 py-1.5 rounded-full mb-5`}
          >
            {t("compareV2.badge")}
          </span>

          <h2>
            <span
              className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight"
              style={interFont}
            >
              {t("compareV2.title1")}{" "}
              <span className="text-meno-pink">
                {t("compareV2.titleAccent")}
              </span>
            </span>
          </h2>
        </motion.div>

        {/* Desktop table */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="hidden lg:block"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div
            className="grid"
            style={{
              gridTemplateColumns:
                "minmax(0, 1fr) minmax(300px, 400px) minmax(300px, 400px)",
            }}
          >
            {/* Column headers */}
            <div />
            <div
              className="rounded-t-2xl bg-gray-100 px-6 py-5 text-center"
              style={interFont}
            >
              <span className="text-base font-semibold text-gray-500">
                {t("compareV2.oldWay")}
              </span>
            </div>
            <div
              className="rounded-t-2xl bg-meno-pink px-6 py-5 text-center"
              style={interFont}
            >
              <span className="text-base font-bold text-white">
                {t("compareV2.withMeno")}
              </span>
            </div>

            {/* Rows */}
            {ROW_KEYS.map((key, i) => {
              return (
                <React.Fragment key={key}>
                  <div
                    className={`flex items-center py-5 border-b border-gray-200 ${textAlign}`}
                    style={interFont}
                  >
                    <span className="text-base font-bold text-gray-900">
                      {t(`compareV2.${key}.label`)}
                    </span>
                  </div>

                  <div
                    className={`flex items-center gap-2.5 bg-gray-50 px-6 py-5 border-b border-gray-200`}
                    style={interFont}
                  >
                    <X
                      aria-hidden="true"
                      focusable="false"
                      className="h-4 w-4 shrink-0 text-gray-300"
                      strokeWidth={2.5}
                    />
                    <span className="text-sm text-gray-400">
                      {t(`compareV2.${key}.old`)}
                    </span>
                  </div>

                  <div
                    className={`flex items-center gap-2.5 bg-[#FFF0F7] px-6 py-5 border-b border-gray-200`}
                    style={interFont}
                  >
                    <Check
                      aria-hidden="true"
                      focusable="false"
                      className="h-4 w-4 shrink-0 text-meno-pink"
                      strokeWidth={3}
                    />
                    <span className="text-sm font-bold text-meno-pink">
                      {t(`compareV2.${key}.meno`)}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        {/* Mobile cards */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col gap-4 lg:hidden"
        >
          {ROW_KEYS.map((key) => (
            <div
              key={key}
              className="overflow-hidden rounded-2xl border border-gray-100"
              style={interFont}
            >
              <div
                className={`bg-white px-5 py-4 border-b border-gray-200 ${textAlign}`}
              >
                <span className="text-sm font-bold text-gray-900">
                  {t(`compareV2.${key}.label`)}
                </span>
              </div>

              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div className={`bg-gray-50 px-4 py-3.5 ${textAlign}`}>
                  <div
                    className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400`}
                  >
                    {t("compareV2.oldWay")}
                  </div>
                  <div
                    className={`flex items-start gap-2`}
                  >
                    <X
                      aria-hidden="true"
                      focusable="false"
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-300"
                      strokeWidth={2.5}
                    />
                    <span className="text-xs text-gray-400">
                      {t(`compareV2.${key}.old`)}
                    </span>
                  </div>
                </div>

                <div className={`bg-[#FFF0F7] px-4 py-3.5 ${textAlign}`}>
                  <div
                    className={`mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#FF00A4]`}
                  >
                    {t("compareV2.withMeno")}
                  </div>
                  <div
                    className={`flex items-start gap-2`}
                  >
                    <Check
                      aria-hidden="true"
                      focusable="false"
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FF00A4]"
                      strokeWidth={3}
                    />
                    <span className="text-xs font-bold text-[#FF00A4]">
                      {t(`compareV2.${key}.meno`)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
